import { useMemo, useEffect } from "react";
import { useProducts } from "@/components/product-step/useProducts";
import { BookingLanguage } from "@/types/booking";
import { FormValues } from "@/components/booking-form/types";
import { useBookingStore } from "@/stores/bookingStore";
import { useMarket } from "@/hooks/useMarket";
import { useTranslation } from "react-i18next";
import { languageMap } from "@/i18n";

interface UseMainStepDataProps {
  selectedProducts: string[];
  selectedAddOns: string[];
  language: BookingLanguage;
  formData?: Partial<FormValues>;
}

export const useMainStepData = ({
  selectedProducts,
  selectedAddOns,
  language,
  formData: passedFormData,
}: UseMainStepDataProps) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { market } = useMarket();
  const { data: productsData } = useProducts(language, market);
  const allProducts = productsData?.products || [];

  // Get discount from booking store
  const discount = useBookingStore((state) => state.discount);
  const clearInvitationCode = useBookingStore(
    (state) => state.clearInvitationCode,
  );

  const selectedProductDetails = useMemo(() => {
    return allProducts.filter((product) =>
      selectedProducts.includes(product.id),
    );
  }, [allProducts, selectedProducts]);

  const selectedAddOnDetails = useMemo(() => {
    return allProducts
      .filter((product) => selectedAddOns.includes(product.id))
      .sort((a, b) => {
        // Sort by sort_order if available
        if (a.sort_order != null && b.sort_order != null) {
          return a.sort_order - b.sort_order;
        }

        // If only one has sort_order, prioritize it
        if (a.sort_order != null && b.sort_order == null) return -1;
        if (a.sort_order == null && b.sort_order != null) return 1;

        // Fallback to alphabetical sorting
        return a.name.localeCompare(b.name);
      });
  }, [allProducts, selectedAddOns]);

  // Combine selected products and add-ons for display purposes
  const allSelectedProductDetails = useMemo(() => {
    return [...selectedProductDetails, ...selectedAddOnDetails];
  }, [selectedProductDetails, selectedAddOnDetails]);

  const packageComponentMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    selectedProductDetails.forEach((product) => {
      if (product.product_type === "package" && product.components) {
        map[product.id] = product.components;
      }
    });
    return map;
  }, [selectedProductDetails]);

  const formData = passedFormData || {}; // Use passed form data or empty object as fallback

  const priceSummaryTranslations = {
    selectedProducts: t("orderSummary.selectedTests"),
    totalPrice: t("productStep.totalPrice"),
  };

  // Clear invitation code when component unmounts (when leaving details step)
  useEffect(() => {
    return () => {
      console.log("useMainStepData - Clearing invitation code on unmount");
      clearInvitationCode();
    };
  }, [clearInvitationCode]);

  return {
    selectedProductDetails,
    selectedAddOnDetails,
    allSelectedProductDetails, // New combined array for display
    discount,
    formData,
    priceSummaryTranslations,
    packageComponentMap,
    allProducts,
  };
};
